import React, { useContext, useEffect, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import useTranslator from "../../hooks/useTranslator";
import LandLordContext from "../../context/landlord/LandLordContext";
import AuthContext from "../../context/auth/AuthContext";
import { toast } from "../../helper/common";
import provincesData from "../../Utils/provincia_municipios.json";
import streetTypeCodes from "../../Utils/streetTypeCodes.json";
import DatePicker from "react-datepicker";
import {
  DatePickerHeader,
  Button,
  Control,
  Field,
  Icon,
  Modal,
  SearchSelect,
  Select,
  Switch,
  Tabs,
  TextField,
} from "../design";
import "./workspaceModals.css";

const numericOnlyOnKeyDown = (e) => {
  const allowed = [
    "Backspace",
    "Tab",
    "ArrowLeft",
    "ArrowRight",
    "Delete",
    "Enter",
  ];
  if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
};

const isValidDateOfBirth = (value) => {
  const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (!match) return false;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return (
    year >= 1900 &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date <= today
  );
};

const dateOfBirthToDate = (value) => {
  if (!isValidDateOfBirth(value)) return null;
  const [day, month, year] = value.split("/").map(Number);
  return new Date(year, month - 1, day);
};

const dateToDateOfBirth = (date) => {
  if (!date || Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getFullYear()}`;
};

const WorkspaceDateOfBirthPicker = ({ id, value, onChange }) => {
  const { _t } = useTranslator();
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const currentYear = new Date().getFullYear();

  return (
    <div className="native-like-datepicker workspace-date__picker">
      <DatePicker
        selected={dateOfBirthToDate(value)}
        open={isCalendarOpen}
        onInputClick={() => setIsCalendarOpen(true)}
        onClickOutside={() => setIsCalendarOpen(false)}
        onSelect={() => setIsCalendarOpen(false)}
        onChange={(date) => onChange(dateToDateOfBirth(date))}
        onChangeRaw={(e) => {
          if (
            e.target.value &&
            (!e.nativeEvent.inputType ||
              !e.nativeEvent.inputType.startsWith("delete"))
          ) {
            let digits = e.target.value.replace(/\D/g, "").slice(0, 8);
            if (digits.length >= 5) {
              digits = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
            } else if (digits.length >= 3) {
              digits = `${digits.slice(0, 2)}/${digits.slice(2)}`;
            }
            e.target.value = digits;
          }
        }}
        dateFormat={["dd/MM/yyyy", "ddMMyyyy", "dd-MM-yyyy", "dd.MM.yyyy"]}
        minDate={new Date("1900-01-01")}
        maxDate={new Date()}
        placeholderText="dd/mm/yyyy"
        id={id}
        className="ds-control__input"
        showPopperArrow={false}
        popperPlacement="bottom-start"
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        openToDate={
          dateOfBirthToDate(value) || new Date(currentYear - 25, 0, 1)
        }
        renderCustomHeader={({ date, changeYear, changeMonth }) => (
          <DatePickerHeader
            date={date}
            changeYear={changeYear}
            changeMonth={changeMonth}
            firstYear={1900}
            lastYear={currentYear}
          />
        )}
      >
        <div className="datepicker-footer">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange("");
              setIsCalendarOpen(false);
            }}
          >
            Clear
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onChange(dateToDateOfBirth(new Date()));
              setIsCalendarOpen(false);
            }}
          >
            Today
          </Button>
        </div>
      </DatePicker>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label={_t("dateofbirth")}
        iconStart={<Icon name="calendar" size={18} />}
        onClick={() => setIsCalendarOpen((prev) => !prev)}
      />
    </div>
  );
};

const PERSON_TYPE_OPTIONS = [
  { title: "individual", icon: "/images/user-icon.svg", key: "individual" },
  { title: "company", icon: "/images/building-icon.svg", key: "company" },
];

const DOCUMENT_TYPE_OPTIONS = [
  { label: "selectDocumentType", value: "" },
  { label: "dni", value: "dni" },
  { label: "nie", value: "nie" },
  { label: "passport", value: "passport" },
];

// Mirrors PurchaseInsurance.jsx's FIELDS_INDIVIDUAL / FIELDS_COMPANY, minus contact fields
const FIELDS_INDIVIDUAL = [
  {
    label: "documentType",
    type: "select",
    options: DOCUMENT_TYPE_OPTIONS,
    name: "document_type",
  },
  {
    label: "documentNumber",
    placeholder: "enterDocumentNumber",
    name: "document_number",
  },
  { label: "firstName", placeholder: "enterFirstName", name: "first_name" },
  { label: "lastName", placeholder: "enterLastName", name: "last_name" },
  {
    label: "dateofbirth",
    type: "date",
    name: "date_of_birth",
    class: "full-width",
  },
];

const FIELDS_COMPANY = [
  { label: "enterCif", placeholder: "enterCif", name: "document_number" },
  { label: "companyName", placeholder: "enterCompanyName", name: "first_name" },
];

const EditBeneficiaryModal = ({ show, onClose }) => {
  const { _t } = useTranslator();
  const { updateWorkspaceBeneficiary } = useContext(LandLordContext);
  const { user, loadUserTenant } = useContext(AuthContext);

  const saved = user?.current_workspace?.beneficiary_details?.[0] ?? {};

  const snapshot = {
    person_type: saved.person_type || "individual",
    document_type: saved.document_type || "",
    document_number: saved.document_number || "",
    first_name: saved.first_name || "",
    last_name: saved.last_name || "",
    date_of_birth: saved.date_of_birth || "",
    province: saved.province || "",
    city: saved.city || "",
    street_type: saved.street_type || "",
    street_name: saved.street_name || "",
    street_number: saved.street_number || "",
    postcode: saved.postcode || "",
    block: saved.block || "",
    ladder: saved.ladder || "",
    floor: saved.floor || "",
    door: saved.door || "",
    additional_address: saved.additional_address || "",
    gran_tenedor: saved.gran_tenedor === true,
  };

  const [personType, setPersonType] = useState(snapshot.person_type);
  const [beneficiaryData, setBeneficiaryData] = useState({
    document_type: snapshot.document_type,
    document_number: snapshot.document_number,
    first_name: snapshot.first_name,
    last_name: snapshot.last_name,
    date_of_birth: snapshot.date_of_birth,
    province: snapshot.province,
    city: snapshot.city,
    street_type: snapshot.street_type,
    street_name: snapshot.street_name,
    street_number: snapshot.street_number,
    postcode: snapshot.postcode,
    block: snapshot.block,
    ladder: snapshot.ladder,
    floor: snapshot.floor,
    door: snapshot.door,
    additional_address: snapshot.additional_address,
    gran_tenedor: snapshot.gran_tenedor,
  });
  const [saving, setSaving] = useState(false);
  const [validationAttempted, setValidationAttempted] = useState(false);

  useEffect(() => {
    if (show) setValidationAttempted(false);
  }, [show]);

  const handleClose = () => {
    setValidationAttempted(false);
    onClose?.();
  };

  const setField = (name, value) =>
    setBeneficiaryData((d) => ({ ...d, [name]: value }));

  const handleProvinceChange = (selected) =>
    setBeneficiaryData((d) => ({
      ...d,
      province: selected?.value || "",
      city: "",
    }));

  const handleFloorChange = (e) => {
    let value = e.target.value.replace(/\D/g, "");
    if (value !== "" && Number(value) > 40) value = "40";
    setField("floor", value);
  };

  const handlePersonTypeChange = (key) => {
    setValidationAttempted(false);
    setPersonType(key);
    setBeneficiaryData((d) => ({
      ...d,
      document_type: key === "company" ? "cif" : "",
      document_number: "",
      first_name: "",
      last_name: "",
      date_of_birth: "",
    }));
  };

  const activeFields =
    personType === "company" ? FIELDS_COMPANY : FIELDS_INDIVIDUAL;

  const {
    document_type,
    document_number,
    first_name,
    last_name,
    date_of_birth,
    province,
    city,
    street_type,
    street_name,
    street_number,
    postcode,
    block,
    ladder,
    floor,
    door,
    gran_tenedor,
  } = beneficiaryData;

  const cityOptions = province
    ? provincesData
        .find((p) => p.Provincia === province)
        ?.municipios.map((m) => ({
          label: m,
          value: m,
        })) || []
    : [];

  const errors = {};
  const required = (name, value, key) => {
    if (!String(value ?? "").trim()) errors[name] = _t(key);
  };

  required("province", province, "provinceIsRequired");
  required("city", city, "municipalityIsRequired");
  required("street_type", street_type, "streetTypeIsRequired");
  required("street_name", street_name, "streetNameIsRequired");
  required("street_number", street_number, "streetNumberIsRequired");
  required("postcode", postcode, "postcodeIsRequired");

  const selectedProvince = provincesData.find(
    (item) => item.Provincia === province
  );
  if (province && !selectedProvince) errors.province = _t("invalidProvince");
  if (
    city &&
    (!selectedProvince || !selectedProvince.municipios.includes(city))
  ) {
    errors.city = _t("invalidMunicipality");
  }
  if (street_type && !Object.values(streetTypeCodes).includes(street_type)) {
    errors.street_type = _t("invalidStreetType");
  }
  if (postcode && !/^\d{5}$/.test(postcode.trim())) {
    errors.postcode = _t("postalCodeMustBeFiveDigits");
  }

  if (personType === "company") {
    required("document_number", document_number, "documentNumberIsRequired");
    required("first_name", first_name, "companyNameIsRequired");
  } else {
    required("document_type", document_type, "documentTypeIsRequired");
    required("document_number", document_number, "documentNumberIsRequired");
    required("first_name", first_name, "firstNameIsRequired");
    required("last_name", last_name, "lastNameIsRequired");
    if (!date_of_birth) {
      errors.date_of_birth = _t("dateOfBirthIsRequired");
    } else if (!isValidDateOfBirth(date_of_birth)) {
      errors.date_of_birth = _t("invalidDateOfBirth");
    }
  }

  const isComplete = Object.keys(errors).length === 0;

  const hasChanges =
    personType !== snapshot.person_type ||
    Object.keys(beneficiaryData).some(
      (key) => beneficiaryData[key] !== snapshot[key]
    );

  const canSave = !saving && (!isComplete || hasChanges);

  const handleSave = async () => {
    setValidationAttempted(true);
    if (!isComplete || !hasChanges) return;
    try {
      setSaving(true);
      await updateWorkspaceBeneficiary({
        beneficiaries: [
          {
            person_type: personType,
            ...beneficiaryData,
          },
        ],
      });
      await loadUserTenant("landlord");
      toast.success(_t("beneficiaryDetailsSaved"));
      handleClose();
    } catch (err) {
      console.log(err);
      toast.error(_t("errorWhileSavingBeneficiaryDetails"));
    } finally {
      setSaving(false);
    }
  };

  const showError = (name) => (validationAttempted ? errors[name] : undefined);
  const requiredLabel = (key) => (
    <>
      {_t(key)} <span className="workspace-required">*</span>
    </>
  );
  const textField = (name, labelKey, props = {}) => (
    <TextField
      id={`beneficiary-${name}`}
      name={name}
      label={props.optional ? _t(labelKey) : requiredLabel(labelKey)}
      value={beneficiaryData[name]}
      onChange={(e) => setField(name, e.target.value)}
      error={props.optional ? undefined : showError(name)}
      {...props.inputProps}
    />
  );

  return (
    <Modal
      open={show}
      onClose={handleClose}
      size="wide"
      align="start"
      title={
        <span className="workspace-title-with-info">
          {_t("beneficiaryInformation")}
          <span
            className="workspace-title-info"
            data-tooltip-id="workspace-beneficiary-info-tooltip"
          >
            <Icon name="info" size={18} />
          </span>
          <ReactTooltip
            id="workspace-beneficiary-info-tooltip"
            noArrow
            className="react-tooltip-styling details-skor-tooltip"
            render={() => <p>{_t("workspaceBeneficiaryDetailsTooltip")}</p>}
          />
        </span>
      }
      actionsLayout="end"
      actions={
        <>
          <Button variant="secondary" size="lg" onClick={handleClose}>
            {_t("cancel")}
          </Button>
          <Button
            size="lg"
            onClick={handleSave}
            disabled={!canSave}
            loading={saving}
          >
            {_t("save")}
          </Button>
        </>
      }
    >
      <Tabs
        className="workspace-tabs"
        value={personType}
        onChange={handlePersonTypeChange}
        items={PERSON_TYPE_OPTIONS.map((opt) => ({
          value: opt.key,
          label: _t(opt.title),
          icon: <img src={opt.icon} alt="" />,
        }))}
      />

      <h3 className="workspace-section-title">{_t("identification")}</h3>
      <div className="workspace-form-grid">
        {activeFields.map((field) =>
          field.type === "select" ? (
            <Select
              key={field.name}
              id={`beneficiary-${field.name}`}
              name={field.name}
              label={requiredLabel(field.label)}
              value={beneficiaryData[field.name]}
              onChange={(e) => setField(field.name, e.target.value)}
              error={showError(field.name)}
              placeholder={_t(field.options[0].label)}
              options={field.options
                .filter((option) => option.value)
                .map((option) => ({
                  value: option.value,
                  label: _t(option.label),
                }))}
            />
          ) : field.type === "date" ? (
            <Field
              key={field.name}
              htmlFor={`beneficiary-${field.name}`}
              label={requiredLabel(field.label)}
              error={showError(field.name)}
              className="workspace-form-grid__wide"
            >
              <Control className="workspace-date">
                <WorkspaceDateOfBirthPicker
                  id={`beneficiary-${field.name}`}
                  value={beneficiaryData[field.name]}
                  onChange={(value) => setField(field.name, value)}
                />
              </Control>
            </Field>
          ) : (
            <TextField
              key={field.name}
              id={`beneficiary-${field.name}`}
              name={field.name}
              label={requiredLabel(field.label)}
              placeholder={_t(field.placeholder)}
              value={beneficiaryData[field.name]}
              onChange={(e) => setField(field.name, e.target.value)}
              error={showError(field.name)}
            />
          )
        )}
      </div>

      <h3 className="workspace-section-title">{_t("address")}</h3>
      <div className="workspace-form-grid">
        <SearchSelect
          id="beneficiary-province"
          label={requiredLabel("province")}
          placeholder={_t("selectProvince")}
          value={province}
          onChange={(value) => handleProvinceChange({ value })}
          options={provincesData.map((p) => ({
            label: p.Provincia,
            value: p.Provincia,
          }))}
          error={showError("province")}
        />
        <SearchSelect
          id="beneficiary-city"
          label={requiredLabel("municipality")}
          placeholder={
            province ? _t("selectMunicipality") : _t("selectProvinceFirst")
          }
          value={city}
          onChange={(value) => setField("city", value)}
          options={cityOptions}
          disabled={!province}
          error={showError("city")}
        />
        <SearchSelect
          id="beneficiary-street-type"
          label={requiredLabel("typeOfRoad")}
          placeholder={_t("selectType")}
          value={street_type}
          onChange={(value) => setField("street_type", value)}
          options={Object.keys(streetTypeCodes).map((type) => ({
            label: type.charAt(0).toUpperCase() + type.slice(1),
            value: streetTypeCodes[type],
          }))}
          error={showError("street_type")}
        />
        {textField("street_name", "streetName", {
          inputProps: { placeholder: _t("enterStreetName") },
        })}
        {textField("street_number", "number", {
          inputProps: {
            placeholder: _t("eG5"),
            onKeyDown: numericOnlyOnKeyDown,
          },
        })}
        {textField("postcode", "postalCode", {
          inputProps: {
            placeholder: "28006",
            maxLength: 5,
            inputMode: "numeric",
            onKeyDown: numericOnlyOnKeyDown,
          },
        })}
      </div>
      <div className="workspace-form-grid workspace-form-grid--4">
        {textField("block", "block", { optional: true })}
        {textField("ladder", "ladder", { optional: true })}
        <TextField
          id="beneficiary-floor"
          name="floor"
          label={_t("floor")}
          value={floor}
          inputMode="numeric"
          onChange={handleFloorChange}
          onKeyDown={numericOnlyOnKeyDown}
        />
        {textField("door", "door", { optional: true })}
      </div>

      <Switch
        id="beneficiary-gran-tenedor"
        label={_t("granTenedor")}
        description={_t("granTenedorSubtitle")}
        checked={gran_tenedor}
        onChange={(checked) => setField("gran_tenedor", checked)}
      />
    </Modal>
  );
};

export default EditBeneficiaryModal;
